/* eslint-disable jira/filename-case, max-classes-per-file, jira/naming/disallow-filenames */
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { DynamicConfig } from '@atlaskit/feature-gate-js-client/dynamic-config';
import type { LocalOverrides } from '@atlaskit/feature-gate-js-client/persistent-override-adapter';
import {
	addFeatureFlagAccessed,
	type FeatureFlagValue,
} from '@atlaskit/react-ufo/feature-flags-accessed';
import { captureException } from '@jira/platform__observability-capture-exception/src/index.tsx';
import { UNSAFE_initIfNeeded } from '@jira/platform__utils-feature-gates-unsafe-init/src/index.tsx';
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';

type WindowFlagVars = {
	__SSR_SYNC_READ_EXP_VAL__:
		| undefined
		| ((experimentName: string, param: string, defaultValue: unknown) => unknown);
	__SSR_SYNC_DYNAMIC_CONFIG_VALUE__:
		| undefined
		| ((experimentName: string) => Record<string, unknown> | undefined);
	_statsigValuesReady: boolean | undefined;
	__SSR_STATSIG_OVERRIDES__:
		| (LocalOverrides & {
				allGatesTrue: boolean;
				allExpValTrue: boolean;
		  })
		| undefined;
};

type AssurePrimitives<T> = T extends string
	? string
	: T extends number
		? number
		: T extends boolean
			? boolean
			: T;

class ErrorPointingUp extends Error {
	constructor(message: string) {
		super(message);
		if (this.stack) {
			const stackLines = this.stack.split('\n');
			// 1 - checkIfExperimentExists
			// 2 - a caller
			this.stack = [...stackLines.slice(2)].join('\n');
		}
	}
}

const checkedExperiments = new Set<string>();
const checkIfExperimentExists = (experimentName: string) => {
	// perform early exist to minimize performance impact
	if (checkedExperiments.has(experimentName)) {
		return;
	}
	checkedExperiments.add(experimentName);
	if (
		FeatureGates.checkGate('jfp_magma_jira-report-incorrect-experiments', {
			fireGateExposure: false,
		})
	) {
		if (!FeatureGates.isExperimentExists(experimentName)) {
			const error = new ErrorPointingUp(`Experiment ${experimentName} does not exist`);
			captureException('FeatureGates.isExperimentExists', error);
			// eslint-disable-next-line no-console
			console.error(`Experiment ${experimentName} does not exist`);
		}
	}
};

function readExpVal<T>(
	experimentName: string,
	param: string,
	defaultValue: T,
): AssurePrimitives<T> {
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const globalVar = (typeof window !== 'undefined'
		? window
		: globalThis) as unknown as WindowFlagVars;

	/**
	 * On server: These overrides are set by the server-side code in jira/src/entry-fs
	 * On client: Check SSR overrides on client-side for shareable debug URLs
	 */
	if (globalVar?.__SSR_STATSIG_OVERRIDES__?.configs?.[experimentName]?.[param] !== undefined) {
		const value = globalVar.__SSR_STATSIG_OVERRIDES__.configs[experimentName][param];
		// Still record the access so overridden experiments remain observable (e.g. SSR smoke-test
		// bisect relies on __SSR_FEATURE_FLAGS_ACCESSED__ to see which experiments a route evaluated).
		// oxlint-disable-next-line typescript/consistent-type-assertions
		addFeatureFlagAccessed(`${experimentName}:${param}`, value as FeatureFlagValue);
		// oxlint-disable-next-line typescript/consistent-type-assertions
		return value as AssurePrimitives<T>;
	}

	if (__SERVER__) {
		if (globalVar?.__SSR_STATSIG_OVERRIDES__?.allExpValTrue) {
			if (param === 'isEnabled' && typeof defaultValue === 'boolean') {
				// oxlint-disable-next-line typescript/consistent-type-assertions
				addFeatureFlagAccessed(`${experimentName}:${param}`, true as FeatureFlagValue);
				// oxlint-disable-next-line typescript/consistent-type-assertions
				return true as AssurePrimitives<T>;
			}
		}

		if (
			typeof globalVar.__SSR_SYNC_READ_EXP_VAL__ === 'function' &&
			// if global variable is ready, then don't reach out to native
			// API, stay within js runtime is faster
			!globalVar._statsigValuesReady
		) {
			try {
				const value = globalVar.__SSR_SYNC_READ_EXP_VAL__(experimentName, param, defaultValue);
				// oxlint-disable-next-line typescript/consistent-type-assertions
				addFeatureFlagAccessed(`${experimentName}:${param}`, value as FeatureFlagValue);
				// oxlint-disable-next-line typescript/consistent-type-assertions
				return value as AssurePrimitives<T>;
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(
					JSON.stringify({
						// oxlint-disable-next-line typescript/consistent-type-assertions
						message: (error as Error).message,
						// oxlint-disable-next-line typescript/consistent-type-assertions
						stack: (error as Error).stack,
					}),
				);
			}
		}
	}

	// Check if the CRITERION override mechanism is available
	if (typeof globalThis?.window?.__CRITERION__?.getExperimentValueOverride === 'function') {
		const overrideValue = globalThis.window.__CRITERION__.getExperimentValueOverride<T>(
			experimentName,
			param,
		);
		if (overrideValue !== undefined && overrideValue !== null) {
			addFeatureFlagAccessed(`${experimentName}:${param}`, overrideValue);

			// oxlint-disable-next-line typescript/consistent-type-assertions
			return overrideValue as AssurePrimitives<T>;
		}
	}

	// Handle vendorless scripts use of gates
	UNSAFE_initIfNeeded();

	const value = FeatureGates.getExperimentValue(experimentName, param, defaultValue);
	// oxlint-disable-next-line typescript/consistent-type-assertions
	addFeatureFlagAccessed(`${experimentName}:${param}`, value as FeatureFlagValue);

	// oxlint-disable-next-line typescript/consistent-type-assertions
	return value as AssurePrimitives<T>;
}

/**
 * Returns the value of a given parameter in an experiment config.
 *
 * @param {string} experimentName - The name of the experiment
 * @param {string} param - The name of the parameter to fetch from the experiment config
 * @param {T} defaultValue - The value to serve if the experiment or parameter do not exist, or if the returned value does not match the expected type.
 */
export function expVal<T>(
	experimentName: string,
	param: string,
	defaultValue: T,
): AssurePrimitives<T> {
	const experiment = readExpVal(experimentName, param, defaultValue);
	checkIfExperimentExists(experimentName);
	return experiment;
}

/**
 * @deprecated
 * Please change your usage to provide a default value to avoid unexpected
 * behavior when the experiment has not yet started.
 *
 * Checks if experiment parameter value equals provided comparator value.
 *
 * @param experimentName - The name of the experiment
 * @param param - The name of the parameter to fetch from the experiment config
 * @param comparatorValue - Value to compare against experiment parameter value
 */
export function expValEquals<T>(
	experimentName: string,
	param: string,
	comparatorValue: T,
	defaultValue?: never,
): boolean;
/**
 * Checks if experiment parameter value equals provided comparator value.
 *
 * __Note__: Uses `null` as experiment parameter default value.
 *
 * @param experimentName - The name of the experiment
 * @param param - The name of the parameter to fetch from the experiment config
 * @param comparatorValue - Value to compare against experiment parameter value
 */
export function expValEquals<T>(
	experimentName: string,
	param: string,
	comparatorValue: T,
	defaultValue: T,
): boolean;
export function expValEquals<T>(
	experimentName: string,
	param: string,
	comparatorValue: T,
	defaultValue: T | null = null,
): boolean {
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const globalVar = (typeof window !== 'undefined'
		? window
		: globalThis) as unknown as WindowFlagVars;

	if (__SERVER__) {
		if (globalVar?.__SSR_STATSIG_OVERRIDES__?.allExpValTrue) {
			// `allExpValTrue` forces the comparison to behave as if the parameter equals the comparator,
			// so the effective value read for this experiment is `comparatorValue`. Record the access so
			// overridden experiments remain observable (e.g. SSR smoke-test bisect relies on
			// __SSR_FEATURE_FLAGS_ACCESSED__ to see which experiments a route evaluated).
			// oxlint-disable-next-line typescript/consistent-type-assertions
			addFeatureFlagAccessed(`${experimentName}:${param}`, comparatorValue as FeatureFlagValue);
			return defaultValue !== comparatorValue;
		}
	}

	const experiment = readExpVal(experimentName, param, defaultValue);
	checkIfExperimentExists(experimentName);
	return experiment === comparatorValue;
}

class ExperimentConfig {
	readonly _experimentName: string;

	readonly _config: DynamicConfig;

	constructor(experimentName: string, config: DynamicConfig) {
		this._experimentName = experimentName;
		this._config = config;
	}

	public get<T>(
		key: string,
		defaultValue: T,
		typeGuard?: (value: unknown) => value is T,
	): AssurePrimitives<T> {
		// Check if the CRITERION override mechanism is available
		if (typeof globalThis?.window?.__CRITERION__?.getExperimentValueOverride === 'function') {
			const overrideValue = globalThis.window.__CRITERION__.getExperimentValueOverride<T>(
				this._experimentName,
				key,
			);
			if (overrideValue !== undefined && overrideValue !== null) {
				addFeatureFlagAccessed(`${this._experimentName}:${key}`, overrideValue);

				// oxlint-disable-next-line typescript/consistent-type-assertions
				return overrideValue as AssurePrimitives<T>;
			}
		}

		const value = this._config.get(key, defaultValue, typeGuard);
		// oxlint-disable-next-line typescript/consistent-type-assertions
		addFeatureFlagAccessed(`${this._experimentName}:${key}`, value as FeatureFlagValue);

		// oxlint-disable-next-line typescript/consistent-type-assertions
		return value as AssurePrimitives<T>;
	}
}

const cache = new Map();
autoBeforeEachTest(() => {
	checkedExperiments.clear();
	cache.clear();
});
/**
 * Returns the config for an experiment, and a function to manually fire exposure.
 *
 * @param {string} experimentName - The name of the experiment
 */
export function UNSAFE_noExposureExp(
	experimentName: string,
): [config: ExperimentConfig, fireExposure: () => void] {
	let instance = cache.get(experimentName);
	const fireExposure = () => FeatureGates.manuallyLogExperimentExposure(experimentName);

	if (instance == null) {
		if (__SERVER__) {
			// oxlint-disable-next-line typescript/consistent-type-assertions
			const globalVar = (typeof window !== 'undefined'
				? window
				: globalThis) as unknown as WindowFlagVars;

			if (
				typeof globalVar.__SSR_SYNC_DYNAMIC_CONFIG_VALUE__ === 'function' &&
				// if global variable is ready, then don't reach out to native
				// API, stay within js runtime is faster
				!globalVar._statsigValuesReady
			) {
				// Handle vendorless scripts use of gates
				try {
					const config = globalVar.__SSR_SYNC_DYNAMIC_CONFIG_VALUE__(experimentName);
					if (typeof config !== 'undefined') {
						config.get = (key: string) => {
							// oxlint-disable-next-line typescript/consistent-type-assertions, @typescript-eslint/no-explicit-any
							const value = (config as any).value[key];
							return value;
						};
						instance = [
							// oxlint-disable-next-line typescript/consistent-type-assertions, @typescript-eslint/no-explicit-any
							new ExperimentConfig(experimentName, DynamicConfig.fromExperiment(config as any)),
							fireExposure,
						];
						cache.set(experimentName, instance);
						return instance;
					}
				} catch (error) {
					// eslint-disable-next-line no-console
					console.error(
						JSON.stringify({
							// oxlint-disable-next-line typescript/consistent-type-assertions
							message: (error as Error).message,
							// oxlint-disable-next-line typescript/consistent-type-assertions
							stack: (error as Error).stack,
						}),
					);
				}
			}
		}
		UNSAFE_initIfNeeded();
		const config = FeatureGates.getExperiment(experimentName, {
			fireExperimentExposure: false,
		});

		instance = [new ExperimentConfig(experimentName, config), fireExposure];
		cache.set(experimentName, instance);
	}

	return instance;
}
